'use client';

import {
    GRS_REFINE_QUERY_PARAM,
    LW_REFINE_QUERY_PARAM,
} from '../SearchResultsFacets/constants';
import { getSingleNonDefaultRefinementsCount } from '../SearchResultsFacets/utils';

import { useState } from 'react';

import { generateClearedParams } from '@/components/ClearAllFilters/utils';
import { updateSearchParams } from '@/components/SearchResultsFacets/utils';
import { SEARCH_SERVICE_PROVIDERS } from '@/constants/grs';
import { getKeyValue } from '@/services/content/data';
import { useSearchContext } from '@/services/search/SearchProvider';
import {
    GRSFacetConfigProperties,
    SearchPageConfigProperties,
} from '@/types/contentStack';
import { Box } from '@mui/material';
import { Modal } from '@nxt-costco-com/forge-components';
import {
    ColorGray20,
    ColorGray50,
    ColorGray98,
    SpaceInsetMd,
} from '@nxt-costco-com/forge-design-tokens';
import {
    type ContentStackEntryDataProps,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';
import { Filter } from '@nxt-costco-com/forge-icons';

import { FilterFacetCount, FilterPopupContainer } from './styles';

export function FilterPopup({
    children,
    pageConfig,
    grsFacetConfig,
}: {
    children: React.ReactNode;
    pageConfig: ContentStackEntryDataProps<SearchPageConfigProperties>;
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
}) {
    const { queryParams: searchParamsMap, setQueryParams } = useQueryParams();
    const searchParams = Object.fromEntries(searchParamsMap.entries());
    const [isOpen, setIsOpen] = useState(false);
    const { searchResult, searchService } = useSearchContext();

    if (!searchResult) {
        return null;
    }
    const refinementKey =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS
            ? GRS_REFINE_QUERY_PARAM
            : LW_REFINE_QUERY_PARAM;
    let refine = searchParams[refinementKey];
    if (Array.isArray(refine)) {
        refine = refine[0] as string;
    }

    let nonDefaultRefinementFiltersAppliedCount = 0;
    if (refine) {
        nonDefaultRefinementFiltersAppliedCount =
            getSingleNonDefaultRefinementsCount({
                refine,
                grsFacetConfig,
                searchService,
            });
    }
    const clearAllLabel = getKeyValue('cleartext', pageConfig);
    const filterLabel = getKeyValue('filter', pageConfig);
    const seeResultsLabel = getKeyValue('seeresults', pageConfig);
    const seeSingleResultLabel = getKeyValue('seesingleresult', pageConfig);

    const seeResultsButtonLabel =
        searchResult.pagination.totalDocs === 1
            ? seeSingleResultLabel.replace(
                  '{{resultCount}}',
                  String(searchResult.pagination.totalDocs),
              )
            : seeResultsLabel.replace(
                  '{{resultCount}}',
                  String(searchResult.pagination.totalDocs),
              );

    return (
        <Box>
            <FilterPopupContainer
                onClick={() => {
                    setIsOpen(true);
                }}
                variant="secondary"
                startIcon={<Filter />}
                sx={{
                    border: `1px solid ${ColorGray50}`,
                    color: ColorGray20,
                }}
            >
                {filterLabel}
                {nonDefaultRefinementFiltersAppliedCount ? (
                    <FilterFacetCount>
                        {nonDefaultRefinementFiltersAppliedCount}
                    </FilterFacetCount>
                ) : (
                    ''
                )}
            </FilterPopupContainer>
            <Modal
                buttons={[
                    {
                        text: seeResultsButtonLabel,
                        action: () => setIsOpen(false),
                    },
                    {
                        text: clearAllLabel,
                        action: () => {
                            updateSearchParams(
                                setQueryParams,
                                generateClearedParams(searchParamsMap),
                            );
                            setIsOpen(false);
                        },
                    },
                ]}
                isOpen={isOpen}
                modalTitle={filterLabel}
                onClose={() => setIsOpen(false)}
                paperProps={{
                    sx: {
                        '&.MuiPaper-root.MuiDialog-paper': {
                            borderBottomLeftRadius: '0px',
                            borderBottomRightRadius: '0px',
                        },
                        '& .MuiDialogActions-root': {
                            padding: SpaceInsetMd,
                            background: ColorGray98,
                            boxShadow: '5px 5px 16px 0px rgba(0, 0, 0, 0.50);',
                        },
                    },
                }}
                scroll="paper"
                sx={{
                    '& > div > div > div:first-of-type': {
                        background: '#3071a9',
                        fontSize: '1.5em',
                        '& h2': {
                            color: '#fff',
                        },
                        '& button svg': {
                            color: '#fff',
                        },
                    },
                }}
            >
                {children}
            </Modal>
        </Box>
    );
}
